import { mediaUrls, normalizeMediaItem } from '@/types/media.types';

export interface OfflineSharedBy {
  first_name: string;
  last_name: string;
  logo_url?: string;
  agency_name?: string;
}

export interface OfflineItineraryEntry {
  id: string;
  title: string;
  downloadedAt: string;
  coverImage?: string; // base64 data URI
  data: {
    itinerary: any;
    sharedBy: OfflineSharedBy;
  };
  imageCache: Record<string, string>; // originalUrl → base64 data URI
}

const DB_NAME = 'place2be_offline';
const DB_VERSION = 1;
const STORE = 'itineraries';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest
): Promise<any> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const store = transaction.objectStore(STORE);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function collectImageUrls(itinerary: any): string[] {
  const urls = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v === 'string' && v.startsWith('http')) urls.add(v);
  };

  // La cover è un MediaItem; normalizeMediaItem tollera anche le righe legacy (URL nudo).
  add(normalizeMediaItem(itinerary.image)?.url);

  for (const day of itinerary.itinerary_days ?? []) {
    for (const act of day.activities ?? []) {
      // Tollera entrambi i formati: stringhe legacy e MediaItem.
      mediaUrls(act.media).forEach(add);
      add(act.image);
    }
  }

  return Array.from(urls);
}

export function replaceUrls(obj: any, cache: Record<string, string>): any {
  if (typeof obj === 'string') return cache[obj] ?? obj;
  if (Array.isArray(obj)) return obj.map((v) => replaceUrls(v, cache));
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, replaceUrls(v, cache)])
    );
  }
  return obj;
}

export const OfflineItinerariesService = {
  async save(entry: OfflineItineraryEntry): Promise<void> {
    await tx('readwrite', (store) => store.put(entry));
  },

  async get(id: string): Promise<OfflineItineraryEntry | undefined> {
    return tx('readonly', (store) => store.get(id));
  },

  async list(): Promise<OfflineItineraryEntry[]> {
    return tx('readonly', (store) => store.getAll());
  },

  async remove(id: string): Promise<void> {
    await tx('readwrite', (store) => store.delete(id));
  },

  async isDownloaded(id: string): Promise<boolean> {
    const entry = await OfflineItinerariesService.get(id);
    return !!entry;
  },

  async downloadItinerary(
    itinerary: any,
    sharedBy: OfflineSharedBy,
    onProgress?: (pct: number) => void
  ): Promise<void> {
    const imageUrls = collectImageUrls(itinerary);
    if (sharedBy.logo_url) imageUrls.push(sharedBy.logo_url);

    const imageCache: Record<string, string> = {};
    let done = 0;

    for (const url of imageUrls) {
      const b64 = await fetchImageAsBase64(url);
      if (b64) imageCache[url] = b64;
      done++;
      onProgress?.(Math.round((done / imageUrls.length) * 100));
    }

    onProgress?.(100);

    const coverUrl = normalizeMediaItem(itinerary.image)?.url;
    const coverImage = coverUrl ? (imageCache[coverUrl] ?? undefined) : undefined;

    const entry: OfflineItineraryEntry = {
      id: itinerary.id,
      title: itinerary.title ?? '',
      downloadedAt: new Date().toISOString(),
      coverImage,
      data: { itinerary, sharedBy },
      imageCache,
    };

    await OfflineItinerariesService.save(entry);
  },
};
